import { PE_STATUSES } from "./packagingExecutions"

export const TESTGAZ_STATUS = {
  SUCCESS: "SUCCESS",
  FAIL: "FAIL",
  TODO: "TODO"
}

/**
 * Compute the status of a single test.
 * - SUCCESS if all rows are conforming
 * - FAIL if any row is non-conforming
 * - TODO if test not started yet (no values)
 */
export const computeTestStatus = (test) => {
  if (!test) return TESTGAZ_STATUS.TODO
  if (test.status === TESTGAZ_STATUS.TODO) return TESTGAZ_STATUS.TODO
  const allConform = test.rows.every(({ conformity }) => conformity.o2 && conformity.co2)
  return allConform ? TESTGAZ_STATUS.SUCCESS : TESTGAZ_STATUS.FAIL
}

/**
 * Compute the global status of a list of tests filling one threshold.
 * Priority: FAIL > SUCCESS > TODO
 */
export const computeTresholdStatus = (tests) => {
  const hasSuccess = tests.some(test => test.status === TESTGAZ_STATUS.SUCCESS)
  const hasFail = tests.some(test => test.status === TESTGAZ_STATUS.FAIL)

  if (hasFail) return TESTGAZ_STATUS.FAIL
  if (hasSuccess) return TESTGAZ_STATUS.SUCCESS
  return TESTGAZ_STATUS.TODO
}

/**
 * Group raw tests by type of packaging (BEFORE_START, REUSABLE, etc.)
 */
export const groupTestsByPackagingType = (rawTests = []) => {
  const testsByStep = new Map()
  rawTests.forEach(test => {
    const list = testsByStep.get(test.step) || []
    list.push(test)
    testsByStep.set(test.step, list)
  })
  return testsByStep
}

/**
 * Groups gaz tests by their threshold name.
 */
export const groupTestsByThreshold = (gazTests = []) =>
  gazTests.reduce((map, test) => {
    map.set(test.name, [...(map.get(test.name) || []), test])
    return map
  }, new Map())

/**
 * Build gaz test entries for a Packaging Execution.
 * 
 * Handles only:
 * - BEFORE_START → single test (mandatory only if CAPPED packaging exists)
 * - CAPPED → one test per 600 packagings (threshold-based)
 *
 * Each test includes its computed status, rows, and author.
 * If a test is missing, a TODO entry is generated.
 */
export const getStepGazTests = ({ allStepperOptions, testsByStep, frequency = 600 }) => {
  const gazTests = []

  allStepperOptions.forEach(step => {
    const stepTests = testsByStep.get(step.value) || []

    if (step.value === "BEFORE_START") {
      // Initial a test if none exists yet
      if (stepTests.length === 0) {
        gazTests.push({
          name: step.label,
          status: TESTGAZ_STATUS.TODO,
          rows: [],
          author: null,
        })
      } 
      // If a test exists, use its status
      else {
        stepTests.forEach(test => {
          gazTests.push({
            name: step.label,
            status: test ? computeTestStatus(test) : TESTGAZ_STATUS.TODO,
            rows: test?.rows || [],
            author: test?.author || null,
          })
        })
      }
      return
    }

    if (step.value === "CAPPED") {
      const stepTests = testsByStep.get("CAPPED") || []
      const cappedGroups = buildCappedThresholdGroups(stepTests, frequency)
      const totalPackagings = step.theoreticalNumber || 0

      // Build threshold list: stop before exceeding total, but ensure at least one
      const thresholds = []
      const max = Math.max(frequency, totalPackagings) // ensures at least one iteration
      for (let n = frequency; n < max; n += frequency) {
        thresholds.push(n)
      }

      // If no tests exist yet, create TODO placeholders for each threshold
      if (stepTests.length === 0) {
        thresholds.forEach(n => {
          gazTests.push({
            name: `Barquettage operculé n°${n}`,
            status: TESTGAZ_STATUS.TODO,
            rows: [],
            author: null,
          })
        })
        return
      }

      // Build a map of existing threshold labels
      const existingLabels = new Set(cappedGroups.map(group => group.label))

      // Add groups with actual tests
      cappedGroups.forEach(group => {
        group.tests.forEach(test => {
          gazTests.push({
            name: group.label,
            status: computeTestStatus(test),
            rows: test.rows || [],
            author: test.author || null,
          })
        })
      })

      // Add TODOs for future thresholds that were not tested yet
      thresholds.forEach(n => {
        const label = `Barquettage operculé n°${n}`
        if (!existingLabels.has(label)) {
          gazTests.push({
            name: label,
            status: TESTGAZ_STATUS.TODO,
            rows: [],
            author: null,
          })
        }
      })
    }
  })

  return gazTests
}

/**
 * Main entry point: build the full gaz tests array for a packaging execution.
 * @param {Object} packagingExecution 
 * @param {Array} allStepperOptions 
 */
export const buildGazTests = (packagingExecution, allStepperOptions) => {
  if (!packagingExecution) return []

  const rawTests = packagingExecution.gazTest || []
  const testsByStep = groupTestsByPackagingType(rawTests)

  let gazTests = []

  // Add step tests only if status is todo, in progress or done
  if ([ PE_STATUSES.todo, PE_STATUSES.inProgress, PE_STATUSES.done].includes(packagingExecution.status)) {
    const stepTests = getStepGazTests({ allStepperOptions, testsByStep  })
    gazTests.push(...stepTests)
  }

  // If status is not TODO or DONE, we don't display the "Avant le début du barquettage test"
  const beforeStartLabel = allStepperOptions.find(step => step.value === "BEFORE_START")?.label
  if (packagingExecution.status !== PE_STATUSES.todo && packagingExecution.status !== PE_STATUSES.done) {
    gazTests = gazTests.filter(test => test.name !== beforeStartLabel)
  } else if (packagingExecution.status === PE_STATUSES.todo) {
    gazTests = gazTests.filter(test => test.name === beforeStartLabel)
  }

  return gazTests
}

/**
 * Verify that the packaging execution contains a CAPPED type of packaging.
 */
export const hasCappedPackaging = (packagingExecution) => {
	return (packagingExecution.packagings || []).some(packaging => packaging.type === "CAPPED")
}

/**
 * Build threshold-based groups for CAPPED gaz tests.
 * Each group represents a batch of tests until a SUCCESS is reached.
 * Once a SUCCESS is found, a new threshold group starts (e.g., n°600, n°1200, ...).
 *
 * Example:
 * - If frequency = 600 → groups will be labeled "Barquettage operculé n°600", "n°1200", etc.
 * - Each group contains all tests performed before the next SUCCESS.
 */
const buildCappedThresholdGroups = (cappedTests, frequency = 600) => {
  const groups = []
  let currentGroup = []
  let currentThreshold = frequency

  cappedTests.forEach(test => {
    currentGroup.push(test)

    const isSuccess = computeTestStatus(test) === TESTGAZ_STATUS.SUCCESS
    if (isSuccess) {
      // Push the current threshold group when success is reached
      groups.push({
        label: `Barquettage operculé n°${currentThreshold}`,
        tests: [...currentGroup],
      })
      // Start a new group for the *next* threshold
      currentGroup = []
      currentThreshold += frequency
    }
  })

  // If we still have unsucceeded tests left, push them to the current threshold
  if (currentGroup.length > 0) {
    groups.push({
      label: `Barquettage operculé n°${currentThreshold}`,
      tests: [...currentGroup],
    })
  }

  return groups
}